import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Edit } from '@mui/icons-material'
import { Box, Card, IconButton, TableContainer, Tooltip } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../components/GRTable/GRTable'
import { Store } from '../../../features/store/types/Store'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { StoreMutation, useGetInternalStoresQuery, useSaveStoreMutation } from '../../api/stores'
import { InternalListToolbar } from '../../components/InternalListToolbar/InternalListToolbar'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { StoreEditDialog } from '../../features/stores/components/StoreEditDialog/StoreEditDialog'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

type CustomProps = { onEdit: (store: Store) => void }

type DialogState = { open: boolean; store?: Store }

export const StoresPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-stores'))
  const { t } = useTranslation()
  const { run } = useMutationFeedback()
  const { data: stores, isLoading, isError } = useGetInternalStoresQuery()
  const [saveStore, { isLoading: isSaving }] = useSaveStoreMutation()
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })

  const tableColumns: GRTableColumn<Store, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-common:name'),
        accessor: ({ row }) => row.name,
        sortable: true,
        sortAccessor: 'name',
        // GRTable compares strings in reverse, so DESC lists names A to Z.
        sortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 240 } },
      },
      {
        labelAccessor: t('internal-service-management:stores_ecosystem'),
        accessor: ({ row }) => row.ecosystem,
        sortable: true,
        sortAccessor: 'ecosystem',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left' },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => (
          <Tooltip title={t('common:edit')}>
            <IconButton color="primary" aria-label={t('common:edit')} onClick={() => customTableProps?.onEdit(row)}>
              <Edit />
            </IconButton>
          </Tooltip>
        ),
        cellProps: { align: 'right' },
        headerCellProps: { sx: { width: 80, minWidth: 80 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(() => ({ onEdit: (store) => setDialogState({ open: true, store }) }), [])

  const handleSave = useCallback(
    async (store: StoreMutation) => {
      const saved = await run(saveStore(store).unwrap(), {
        success: t('internal-service-management:stores_saved', { name: store.name }),
        error: t('internal-service-management:stores_save_failed'),
      })
      if (saved.ok) setDialogState({ open: false })
    },
    [run, saveStore, t]
  )

  return (
    <Box>
      <InternalListToolbar add={{ label: t('internal-service-management:stores_add'), onClick: () => setDialogState({ open: true }) }} />

      <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-service-management:stores_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={stores ?? []}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <StoreEditDialog
        open={dialogState.open}
        store={dialogState.store}
        isSaving={isSaving}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
    </Box>
  )
}
